/* Litmus motion layer — removable. Dependency-free, deferred.
   Reads its four switches from data attributes on its own <script> tag, marks
   <html> so assets/litmus-motion.css can act, and hooks only existing class
   names so no approved Liquid has to change.

   Nothing here is required for the page to work. If this file never loads, is
   slow, or throws, the storefront renders exactly as it does today.

   Fix round 2 — 19 Sep 2026 (rendered-QA findings, reviews/h-motion/qa-verdicts.json)
   M1  rest() now knows every main-image markup the theme can render, not just
       Dawn's. The Litmus review gallery has no .product__media-list, so a
       navigation into it left the outgoing snapshot unpaired and frozen on
       screen. Selector inventory with file:line is above rest().
   M2  The reveal "fuse" is a dead-observer detector, not a 2500 ms timer that
       revealed the whole page before the visitor had scrolled. */
(function () {
  'use strict';

  var doc = document;
  var root = doc.documentElement;
  /* Two copies of this script on one page would double every listener. */
  if (root.classList.contains('litmus-motion')) return;

  var tag = doc.currentScript || doc.querySelector('script[data-litmus-motion]');
  var quiet = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  var each = Function.prototype.call.bind(Array.prototype.forEach);

  function on(name) {
    return !tag || tag.getAttribute('data-litmus-motion-' + name) !== 'false';
  }
  function still() {
    return !!(quiet && quiet.matches);
  }
  function watchQuiet(fn) {
    if (quiet && quiet.addEventListener) quiet.addEventListener('change', fn);
  }

  root.classList.add('litmus-motion');

  /* ---------------------------------------------------------------- reveals
     eyebrow -> heading -> intro -> content, 70 ms apart, capped at six steps. */

  var STEPS = [
    '[class*="__eyebrow"]',
    'h2, [class*="__heading"], .litmus-editorial-band__line',
    '[class*="__intro"], [class*="__description"], [class*="__descriptor"], .litmus-editorial-band__text'
  ];
  var FINE = '[class*="__grid"] > *, [class*="__list"] > li, .multicolumn-list__item, .litmus-faq__row';
  var BLOCK =
    '[class*="__media"], [class*="__figure"], [class*="__facts"], [class*="__footnote"], [class*="__actions"], [class*="__content"], [class*="__panel"]';
  /* Never touch a moving slide, a rail, or something Dawn already animates.
     The cart is also excluded outright: `[class*="__content"]` matches Dawn's
     .cart__contents, and a reveal that held would hide the line items, the
     totals and the checkout button behind an observer. */
  var AVOID =
    'litmus-quote-carousel, slider-component, .slider, .scroll-trigger, [data-litmus-reveal], ' +
    '#main-cart-items, #main-cart-footer, cart-drawer, cart-notification, cart-items, cart-drawer-items';

  function reveals() {
    if (still() || !window.IntersectionObserver) return;
    /* The theme editor re-renders sections constantly; a hidden state that
       outlived its observer would read as a broken section to the merchant. */
    if (window.Shopify && window.Shopify.designMode) return;

    var scope = doc.querySelector('#MainContent') || doc.querySelector('main');
    var sections = scope ? scope.querySelectorAll(':scope > .shopify-section') : [];
    if (sections.length < 2) return;

    var groups = [];

    function claim(el, into) {
      if (!el || el.closest(AVOID) || el.querySelector('[data-litmus-reveal]')) return false;
      if (!el.getClientRects().length) return false;
      el.setAttribute('data-litmus-reveal', 'hold');
      into.push(el);
      return true;
    }
    /* One element per step. Several sections wrap the eyebrow, heading and
       intro in a shared container that also matches the intro selector, so walk
       the matches and take the first that is not swallowing an earlier pick. */
    function claimStep(section, selector, into) {
      var all = section.querySelectorAll(selector);
      for (var i = 0; i < all.length; i++) if (claim(all[i], into)) return;
    }

    each(sections, function (section, index) {
      if (!index) return; // never the hero / first section
      var picked = [];
      each(STEPS, function (step) {
        claimStep(section, step, picked);
      });
      each(section.querySelectorAll(FINE), function (el) {
        claim(el, picked);
      });
      each(section.querySelectorAll(BLOCK), function (el) {
        claim(el, picked);
      });

      var items = [];
      var fold = window.innerHeight || 0;
      each(picked, function (el, order) {
        /* Anything the visitor can already see, or has already scrolled past
           (a reload that restored the scroll position, a deep link), is left
           exactly as painted. Only content strictly below the fold is hidden,
           so a deferred script that lands late can never blank something out. */
        if (el.getBoundingClientRect().top < fold) {
          el.removeAttribute('data-litmus-reveal');
          return;
        }
        el.style.setProperty('--litmus-reveal-delay', Math.min(order, 5) * 70 + 'ms');
        el.setAttribute('data-litmus-reveal', 'out');
        items.push(el);
      });
      if (items.length) groups.push({ section: section, items: items });
    });

    if (!groups.length) return;
    root.classList.add('litmus-motion--reveal');

    function show(group) {
      each(group.items, function (el) {
        el.setAttribute('data-litmus-reveal', 'in');
      });
      group.items.length = 0;
    }
    function showAll() {
      each(groups, show);
      clearTimeout(fuse);
      /* Hoisted: showAll can run before the observer exists, or with none. */
      if (io) io.disconnect();
    }
    /* Anything now at or above the fold is shown at once: the fragment scroll
       of a deep link runs after this script, and the observer only reports
       sections that intersect, never ones the jump left behind. The hash target
       is revealed explicitly as well, because a deep link into a section the
       browser has not finished scrolling to would otherwise still be faded. */
    function sweep() {
      var fold = window.innerHeight || 0;
      var hash = location.hash && location.hash.length > 1 ? location.hash.slice(1) : '';
      var target = hash ? doc.getElementById(hash) : null;
      each(groups, function (group) {
        if (!group.items.length) return;
        if (group.section.getBoundingClientRect().top < fold) return show(group);
        if (target && group.section.contains(target)) show(group);
      });
    }

    /* An IntersectionObserver always delivers an initial callback for every
       target it is given, within a frame or two of observe(). So one callback
       of any kind proves it is alive, and the only thing left to guard against
       is an observer that is missing, throws, or never speaks at all. A plain
       timer instead — which is what this was — reveals the whole page 2.5 s
       after load, so a visitor who reads the hero first never sees a reveal. */
    var alive = false;
    var io = null;
    try {
      io = new IntersectionObserver(function (entries) {
        alive = true;
        each(entries, function (entry) {
          if (!entry.isIntersecting) return;
          io.unobserve(entry.target);
          each(groups, function (group) {
            if (group.section === entry.target) show(group);
          });
        });
      }, { rootMargin: '0px 0px -8% 0px' });
      each(groups, function (group) {
        io.observe(group.section);
      });
    } catch (error) {
      io = null;
    }

    /* Dead-observer detector, not a reveal timer: it fires once and only if not
       a single callback has arrived. If they are arriving, nothing is ever
       bulk-revealed and every section waits for the visitor to reach it. */
    var fuse = io ? setTimeout(function () { if (!alive) showAll(); }, 1500) : 0;
    if (!io) showAll();
    window.addEventListener('load', sweep);
    window.addEventListener('hashchange', sweep);
    /* Back/forward cache: the page is restored painted, but a section the
       visitor had not reached is still held out and the observer may not speak
       again. Sweeping reveals what is on screen without touching the rest. */
    window.addEventListener('pageshow', function (event) {
      if (event && event.persisted) sweep();
    });
    window.addEventListener('beforeprint', showAll);
    /* Keyboard focus must never land on something that is still faded out. */
    doc.addEventListener('focusin', function (event) {
      var node = event.target;
      if (!(node instanceof Element)) return;
      each(groups, function (group) {
        if (group.items.length && group.section.contains(node)) show(group);
      });
    });
    watchQuiet(function () {
      if (still()) showAll();
    });
  }

  /* ------------------------------------------- page-to-product image morph */

  /* A link may climb a few levels to the image beside it, but never out of its
     own card, and a text link in the header, the footer or a utility bar may
     not climb at all — the nearest image there is the logo, not a product. */
  var OUTSIDE =
    '.shopify-section-group-header-group, .shopify-section-group-footer-group, .utility-bar, ' +
    '.header-wrapper, .section-header, .footer';
  var EDGE =
    'header, footer, nav, form, dialog, cart-drawer, cart-notification, predictive-search, ' +
    '.search-modal, .header-wrapper, .section-header, .utility-bar, .shopify-section';

  function morph() {
    var held = null;
    var timer = 0;

    function drop() {
      clearTimeout(timer);
      if (!held) return;
      held.style.removeProperty('view-transition-name');
      held = null;
    }
    function hand(el) {
      drop();
      if (!el || still()) return;
      el.style.setProperty('view-transition-name', 'litmus-product-media');
      held = el;
    }
    /* Every main-image markup this theme can render, in document order. Both
       galleries are one slider list at every width — desktop and mobile differ
       only in CSS — so there is no second, viewport-specific variant to name.

       A. Dawn gallery (the default product template, and any Litmus layout that
          is not a review fixture):
          snippets/product-media-gallery.liquid:59-63   ul.product__media-list
          snippets/product-media-gallery.liquid:66-70   li.product__media-item.is-active
          snippets/product-media-gallery.liquid:98-102  li.product__media-item (the rest)
          snippets/product-thumbnail.liquid:86          div.product__media
          snippets/product-thumbnail.liquid:88-105      img (image-magnify-* or the
                                                        stomach routine asset)
          Rendered by sections/main-product.liquid:95 (e.g.
          templates/product.stomach.json, templates/product.json) and by
          sections/featured-product.liquid:79.

       B. Litmus review gallery (templates/product.litmus-review.json and
          product.litmus-faq-review.json, gated at sections/main-product.liquid:3-6
          and rendered at :92-93):
          snippets/litmus-product-review.liquid:14      media-gallery.litmus-review-gallery
          snippets/litmus-product-review.liquid:17      ul.litmus-review-gallery__slides
          snippets/litmus-product-review.liquid:18      li.litmus-review-gallery__slide.is-active
          snippets/litmus-product-review.liquid:21-22   button.litmus-review-gallery__zoom > img
          The second slide (:27, .litmus-review-gallery__missing) is a text
          placeholder with no image, which is why the .is-active branch matters.

       querySelector takes the first list in the document and one image inside
       it, so exactly one element is ever named; no match hands back null and
       nothing is named at all. */
    var MEDIA_LIST = '.product__media-list, .litmus-review-gallery__slides';
    var MEDIA_ACTIVE =
      '.product__media-item.is-active .product__media img, ' +
      '.litmus-review-gallery__slide.is-active img';
    var MEDIA_ANY =
      '.product__media-item .product__media img, .litmus-review-gallery__slide img';

    function rest() {
      var list = doc.querySelector(MEDIA_LIST);
      var img = list && (list.querySelector(MEDIA_ACTIVE) || list.querySelector(MEDIA_ANY));
      hand(img && img.getClientRects().length ? img : null);
    }
    function near(a) {
      var img = a.querySelector('img');
      if (img) return img;
      if (a.closest(OUTSIDE)) return null;
      var node = a.parentElement;
      var hop = 0;
      while (node && hop++ < 4 && node !== doc.body) {
        if (node.matches(EDGE)) return null;
        img = node.querySelector('img');
        if (img) return img;
        node = node.parentElement;
      }
      return null;
    }

    doc.addEventListener('click', function (event) {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      var a = event.target instanceof Element && event.target.closest('a[href]');
      if (!a || a.hasAttribute('download') || (a.target && a.target !== '_self')) return;
      var url;
      try {
        url = new URL(a.href, location.href);
      } catch (error) {
        return;
      }
      if (url.origin !== location.origin || url.pathname.indexOf('/products/') === -1) return;
      var img = near(a);
      if (!img || !img.getClientRects().length) return;
      hand(img);
      /* Navigation cancelled? Hand the name back to this page's own image. */
      timer = setTimeout(rest, 1200);
    });

    window.addEventListener('pageshow', rest);
    window.addEventListener('pagereveal', function (event) {
      rest();
      /* Dawn fades product media in on load (settings.animations_reveal_on_scroll).
         The one image the browser is morphing into must not start transparent,
         so cancel that one fade with Dawn's own class (assets/animations.js:4,
         assets/base.css:3338) and only when a transition is actually running. */
      if (held && event && event.viewTransition) {
        var trigger = held.closest('.scroll-trigger');
        if (trigger) trigger.classList.add('scroll-trigger--cancel');
      }
    });
    /* The theme editor swaps the whole product section on every edit. */
    doc.addEventListener('shopify:section:load', rest);
    watchQuiet(rest);
    rest();
  }

  /* -------------------------------------------------------- header condense */

  function condense() {
    var wrap = null;
    var bar = null;

    function find() {
      wrap = doc.querySelector('sticky-header.litmus-header, sticky-header.header-wrapper');
      bar = wrap ? wrap.closest('.section-header') : null;
      return !!bar;
    }
    if (!find()) return;

    var last = window.pageYOffset || 0;
    var drift = 0;
    var lock = 0;
    var tall = 120;
    var small = false;
    var queued = false;

    /* Measured off the hot path: never inside the scroll callback, so no scroll
       frame ever forces a synchronous layout. */
    function measure() {
      if (small || !bar) return;
      tall = bar.offsetHeight || tall;
    }

    /* A drawer, a dropdown or predictive search is open: leave the header be.
       The cart drawer and the cart notification live outside the header, so
       they are checked on the document, not inside the header wrapper. */
    function busy() {
      if (doc.querySelector('cart-drawer.active, #cart-notification.active')) return true;
      return !!(wrap && wrap.querySelector('details[open], .menu-opening')) || bar.classList.contains('menu-open');
    }
    function set(next) {
      if (next === small) return;
      small = next;
      root.classList.toggle('litmus-header-condensed', next);
      drift = 0;
      lock = Date.now() + 260;
      if (!next) setTimeout(measure, 280);
    }
    function read() {
      queued = false;
      /* Theme editor: the header section is replaced wholesale on every edit. */
      if (bar && !bar.isConnected && !find()) return;
      var y = window.pageYOffset || root.scrollTop || 0;
      var step = y - last;
      last = y;
      if (still()) return set(false);
      if (y <= tall + 24) {
        drift = 0;
        return set(false);
      }
      if (Date.now() < lock || busy() || Math.abs(step) < 6) return;
      drift = drift > 0 === step > 0 ? drift + step : step;
      if (drift >= 24) set(true);
      else if (drift <= -24) set(false);
    }

    measure();
    window.addEventListener('scroll', function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(read);
    }, { passive: true });
    window.addEventListener('resize', measure, { passive: true });
    doc.addEventListener('shopify:section:load', function () {
      find();
      measure();
    });
    watchQuiet(function () {
      if (still()) set(false);
    });
  }

  if (on('hover')) root.classList.add('litmus-motion--hover');
  if (on('header')) {
    root.classList.add('litmus-motion--header');
    condense();
  }
  if (on('view-transitions')) morph();
  if (on('reveal')) reveals();
})();
